/* eslint-disable */
import { useState, useMemo } from 'react';
import { Registration, RegistrationStatusEnum } from '../API';
import useDebouncedValue from './useDebouncedValue';
import { parseAdditionalData } from '../utils/parseAdditionalData';
import { ROWS_PER_PAGE } from '../constants/config';

export type EnrichedRegistration = Registration & {
  membership: string;
  notes: string;
  guests: number;
  checkedInCount: number;
};

export function useAttendeeFiltering(attendees: Registration[]) {
  const [searchQuery, setSearchQuery] = useState('');
  const debouncedQuery = useDebouncedValue(searchQuery, 300);
  const [testToggled, setTestToggled] = useState(false);
  const [invitedToggled, setInvitedToggled] = useState(true);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const [sortConfig, setSortConfig] = useState<{ key: string; direction: string } | null>(null);
  const [currentPage, setCurrentPage] = useState(0);

  const filteredAttendees = useMemo<EnrichedRegistration[]>(
    () =>
      [...attendees]
        .map(attendee => {
          const parsed = parseAdditionalData(attendee.additionalData);
          return {
            ...attendee,
            membership: parsed.membership || '',
            guests: attendee.guests || 0,
            checkedInCount: attendee.checkedInCount ?? 0,
            notes: parsed.notes || '',
          };
        })
        .sort((a, b) => {
          if (sortConfig !== null) {
            const { key, direction } = sortConfig;
            let aValue = a[key];
            let bValue = b[key];
            if (key === 'notes') {
              aValue = (a.notes ?? '').toLowerCase();
              bValue = (b.notes ?? '').toLowerCase();
            } else {
              if (typeof aValue === 'string') aValue = aValue.toLowerCase();
              if (typeof bValue === 'string') bValue = bValue.toLowerCase();
            }
            if (aValue < bValue) return direction === 'ascending' ? -1 : 1;
            if (aValue > bValue) return direction === 'ascending' ? 1 : -1;
          }
          return 0;
        })
        .filter(attendee => {
          const query = debouncedQuery.toLowerCase();
          const firstName = attendee.firstName ? attendee.firstName.toLowerCase() : '';
          const lastName = attendee.lastName ? attendee.lastName.toLowerCase() : '';
          const email = attendee.email ? attendee.email.toLowerCase() : '';
          const fullName = `${firstName} ${lastName}`;
          const matchesQuery = fullName.includes(query) || email.includes(query);
          const showTestRegistrations = testToggled || attendee.live;
          const showInvitedRegistrations = invitedToggled || attendee.registrationStatus !== RegistrationStatusEnum.INVITED;
          const matchesStatus = statusFilter === 'ALL' || attendee.registrationStatus === statusFilter;
          return matchesQuery && showTestRegistrations && showInvitedRegistrations && matchesStatus;
        }),
    [attendees, debouncedQuery, testToggled, invitedToggled, statusFilter, sortConfig]
  );

  const totalPages = Math.ceil(filteredAttendees.length / ROWS_PER_PAGE);

  const paginatedAttendees = useMemo(
    () => filteredAttendees.slice(currentPage * ROWS_PER_PAGE, (currentPage + 1) * ROWS_PER_PAGE),
    [filteredAttendees, currentPage]
  );

  const handleSort = (columnKey: string) => {
    const direction =
      sortConfig?.key === columnKey && sortConfig.direction === 'ascending'
        ? 'descending'
        : 'ascending';
    setSortConfig({ key: columnKey, direction });
  };

  const handleNextPage = () => setCurrentPage(p => Math.min(p + 1, totalPages - 1));
  const handlePreviousPage = () => setCurrentPage(p => Math.max(p - 1, 0));
  const handleTestToggleChange = (checked: boolean) => setTestToggled(checked);
  const handleInvitedToggleChange = (checked: boolean) => setInvitedToggled(checked);

  return {
    searchQuery, setSearchQuery,
    testToggled, handleTestToggleChange,
    invitedToggled, handleInvitedToggleChange,
    statusFilter, setStatusFilter,
    sortConfig, handleSort,
    currentPage, handleNextPage, handlePreviousPage,
    filteredAttendees, paginatedAttendees, totalPages,
  };
}
